import * as stylex from '@stylexjs/stylex'
import { createContext, useContext, useMemo, type ForwardedRef, type PropsWithChildren } from 'react'
import { type System, system } from '../tokens/system.stylex'
import { useResponsive, type conditions } from '../useResponsive/useResponsive'

export type ResponsiveStyleValue<T> = T | Partial<Record<keyof typeof conditions, T | undefined>>
export type GridDirection = 'row' | 'row-reverse' | 'column' | 'column-reverse'
export type GridGap = number | string
export type GridSize = 'auto' | number
export type GridWrap = 'nowrap' | 'wrap' | 'wrap-reverse'

export interface GridProps {
  /** 根元素的 ref */
  rootRef?: ForwardedRef<HTMLDivElement>
  /** 自訂根元素的 StyleX 樣式 */
  rootStyle?: stylex.StyleXStyles
  /** 是否為容器模式（啟用 flex 佈局並提供 context 給子項目） */
  container?: boolean
  /** 欄間距，數值會乘以 system.unit */
  columnGap?: GridGap
  /** 列間距，數值會乘以 system.unit */
  rowGap?: GridGap
  /**
   * 統一間距，同時套用於欄與列間距
   * @default 2
   */
  gap?: GridGap
  /** 項目所佔欄數，支援響應式設定 */
  size?: ResponsiveStyleValue<GridSize>
  /**
   * flex-wrap 屬性
   * @default 'wrap'
   */
  wrap?: GridWrap
  /**
   * flex 排列方向
   * @default 'row'
   */
  direction?: GridDirection
  /**
   * 總欄數
   * @default system.columns
   */
  columns?: number
}

const baseStyles = stylex.create({
  container: (rowGap: GridGap, columnGap: GridGap) => ({
    gap: `calc(${rowGap}) calc(${columnGap})`,
    boxSizing: 'border-box',
    display: 'flex',
    flexGrow: 'wrap',
    minWidth: 0,
  }),
  item: {
    boxSizing: 'border-box',
    flexBasis: 'auto',
    flexGrow: 0,
    minWidth: '0px',
  },
  direction: (direction: GridProps['direction']) => ({
    flexDirection: direction,
  }),
  wrap: (wrap: GridProps['wrap']) => ({
    flexWrap: wrap,
  }),
  sizeAuto: {
    maxWidth: 'none',
    width: 'auto',
  },
  size: (value: number, columnGap: GridGap, columns: number | System['columns']) => ({
    width: `calc(100% * ${value} / ${columns} - (${columns} - ${value}) * (${columnGap} / ${columns}))`,
  }),
})

const getSizeStyle = (value: GridSize, columnGap: GridGap, columns: number | System['columns']) => {
  if (value === 'auto') return baseStyles.sizeAuto
  if (typeof value === 'number') return baseStyles.size(value, columnGap, columns)
}

const GridComponent = (props: PropsWithChildren<GridProps>) => {
  const {
    rootRef,
    rootStyle,
    container,
    columnGap: columnGapProp,
    rowGap: rowGapProp,
    gap = 2,
    size,
    wrap = 'wrap',
    direction = 'row',
    children,
    columns = system.columns,
    ...rest
  } = props
  const rowGap = useMemo(() => `${system.unit} * ${rowGapProp ?? gap}`, [gap, rowGapProp])
  const columnGap = useMemo(() => `${system.unit} * ${columnGapProp ?? gap}`, [gap, columnGapProp])
  /**
   * TODO: should not use media query here consider use css media query to improve performance
   * stylex should provide a way to use css media query more easier
   */
  const isMobile = useResponsive('mobile')
  const isTablet = useResponsive('tablet')
  const isDesktop = useResponsive('desktop')
  const responsiveSizeStyle = useMemo(() => {
    if (size === 'auto' || typeof size === 'number') return getSizeStyle(size, columnGap, columns)
    const { mobile, tablet, desktop } = size ?? {}
    let value: GridSize | undefined
    if (isMobile) value = mobile
    if (isTablet) value = tablet || mobile
    if (isDesktop) value = desktop || tablet || mobile
    return value ? getSizeStyle(value, columnGap, columns) : undefined
  }, [columnGap, size, isMobile, isTablet, isDesktop, columns])

  return (
    <div
      ref={rootRef}
      {...stylex.props(
        container ? baseStyles.container(rowGap, columnGap) : baseStyles.item,
        baseStyles.direction(direction),
        baseStyles.wrap(wrap),
        responsiveSizeStyle,
        rootStyle,
      )}
      {...rest}
    >
      {children}
    </div>
  )
}

const GridContext = createContext<{
  gap?: GridProps['gap']
  rowGap?: GridProps['rowGap']
  columnGap?: GridProps['columnGap']
  columns?: GridProps['columns']
}>({})

const GridItem = (props: PropsWithChildren<GridProps>) => {
  const sharedProps = useContext(GridContext)
  return <GridComponent {...props} {...sharedProps} />
}

const GridContainer = (props: PropsWithChildren<GridProps>) => {
  const { gap, rowGap, columnGap, columns } = props
  const contextValue = useMemo(() => ({ gap, rowGap, columnGap, columns }), [gap, rowGap, columnGap, columns])
  return (
    <GridContext.Provider value={contextValue}>
      <GridComponent container {...props} />
    </GridContext.Provider>
  )
}

/**
 * Grid 是基於 flex 的格線佈局元件，支援響應式欄位設定。
 * 當 container 為 true 時作為容器使用，否則作為子項目使用。
 */
export const Grid = (props: PropsWithChildren<GridProps>) => {
  const { container, ...rest } = props
  if (container) {
    return <GridContainer {...rest} />
  }
  return <GridItem {...rest} />
}
